import { Button, Card, Progress, Typography } from "antd";
import classNames from "classnames";
import Lottie from "lottie-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/axios";
import animation from "../assets/animations/waiting.json";
import { useView } from "../contexts/ViewContext";
import styles from './Waiting.module.css';

const { Title, Text } = Typography;

const formatCountdown = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const Waiting = ({ learningJourneyId, view }) => {
    const progress = view?.view_data?.progress ?? null;
    const message = view?.view_data?.message ?? null;
    const expectedSeconds = view?.view_data?.expected_seconds ?? null;
    const restartMessage = view?.view_data?.restart_message ?? null;
    const restartEndpoint = view?.view_data?.restart_endpoint ?? null;
    const retryInSeconds = view?.view_data?.retry_in_seconds ?? null;
    const title = view?.view_data?.title ?? "Preparing your next step";
    const hasPlan = view?.plan != null;

    const { refreshView } = useView();
    const [restarting, setRestarting] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    // Re-synced on every poll; ticks down locally in between so the countdown moves smoothly.
    const [retryRemaining, setRetryRemaining] = useState(retryInSeconds);

    // The parent only mounts Waiting while view_type === "waiting", so this
    // resets naturally on every transition into (or within) the waiting view --
    // no backend timestamp needed.
    const mountedAtRef = useRef(Date.now());

    useEffect(() => {
        mountedAtRef.current = Date.now();
        setElapsedMs(0);
    }, [view?.view_id]);

    useEffect(() => {
        setRetryRemaining(retryInSeconds);
    }, [retryInSeconds]);

    useEffect(() => {
        const interval = setInterval(() => {
            setElapsedMs(Date.now() - mountedAtRef.current);
            setRetryRemaining((seconds) => (seconds == null ? seconds : Math.max(seconds - 1, 0)));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const expectedMs = expectedSeconds != null ? expectedSeconds * 1000 : null;
    // restart_message is the stop signal: the backend sends it only once it has given up,
    // and only then does the motion settle. restart_endpoint alone also arrives while a
    // retry is scheduled, backing "Try Now".
    const isStopped = restartMessage != null;
    const isRetrying = !isStopped && retryInSeconds != null;
    const isSlow = !isStopped && !isRetrying && expectedMs != null && elapsedMs >= expectedMs;

    let body = message ?? "Sit tight. I'm getting everything ready for you.";

    if (isStopped) {
        body = restartMessage;
    } else if (isRetrying) {
        const retryLine = retryRemaining > 0
            ? `Trying again in ${formatCountdown(retryRemaining)}…`
            : "Trying again…";
        body = [message, retryLine].filter(Boolean).join("\n");
    } else if (isSlow) {
        body = "Still working on it — this is taking longer than usual.";
    }

    const handleRestart = async () => {
        if (!restartEndpoint) {
            return;
        }
        setRestarting(true);
        try {
            await api.post(restartEndpoint, {
                learning_journey_id: learningJourneyId,
            });
            await refreshView();
        } finally {
            setRestarting(false);
        }
    };

    return (
        <div className={classNames(styles.page, { [styles.animated]: !isStopped })}>
            <Card
                className={styles.card}
                variant="outlined"
            >
                <Title level={3} className={styles.title}>
                    {title}
                </Title>
                <Text type="secondary" className={styles.body}>
                    {body.split('\n').map((line, i) => <span key={i}>{line}<br /></span>)}
                </Text>

                <div className={styles.lottieRow}>
                    <div className={classNames(styles.lottieWrapper, { [styles.floating]: !isStopped })}>
                        <Lottie
                            animationData={animation}
                            loop={!isStopped}
                            autoplay
                            style={{ width: "100%", height: "100%" }}
                        />
                    </div>

                    {progress !== null && (
                        <Progress
                            type="circle"
                            percent={progress}
                            size={160}
                            strokeWidth={10}
                            strokeColor={{
                                "0%": "#108ee9",
                                "100%": "#87d068",
                            }}
                        />
                    )}
                </div>

                {(isStopped || isRetrying) && (
                    <div className={styles.actions}>
                        {restartEndpoint && (
                            <Button
                                type={isStopped ? "primary" : "default"}
                                loading={restarting}
                                onClick={handleRestart}
                            >
                                {isStopped ? "Try Again" : "Try Now"}
                            </Button>
                        )}
                        {hasPlan && (
                            <Link to={`/t/${learningJourneyId}/plan`} className={styles.planLink}>
                                View your plan
                            </Link>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default Waiting;
